const VAT = 19;

const toExclVat = (inclVatPrice) =>
  Math.round((inclVatPrice / (1 + VAT / 100)) * 10000) / 10000;

// ── Templates ──────────────────────────────────────────────────────────────────
// These are the canonical skeletons. Never mutate them — buildTariff spreads
// and maps over them so every call produces a fresh object.

const STATIC_TEMPLATE = {
  country_code: "DE",
  party_id: "COC",
  currency: "EUR",
  type: "REGULAR",
  tariff_alt_text: [],
  tariff_alt_url: null,
  min_price: null,
  max_price: null,
  start_date_time: null,
  end_date_time: null,
  last_updated: "",
  elements: [
    {
      context_label: "ACTIVATION",
      price_components: [{ type: "FLAT", price: 0.42, vat: 19, step_size: 1 }],
    },
    {
      context_label: "ENERGY_PRICE",
      price_components: [{ type: "ENERGY", price: 0, vat: 19, step_size: 1 }],
    },
    {
      context_label: "BLOCKING_FEE",
      price_components: [{ type: "TIME", price: 0, vat: 19, step_size: 1 }],
      restrictions: { min_duration: 3600, max_duration: 13769 },
    },
    {
      context_label: "BLOCKING_FEE_CAP",
      price_components: [{ type: "FLAT", price: 20.168067, vat: 19, step_size: 1 }],
      restrictions: { min_duration: 13769 },
    },
  ],
};

const DAY_NIGHT_TEMPLATE = {
  country_code: "DE",
  party_id: "COC",
  currency: "EUR",
  type: "REGULAR",
  tariff_alt_text: [{ language: "en", text: "DAY_NIGHT" }],
  tariff_alt_url: null,
  min_price: null,
  max_price: null,
  start_date_time: null,
  end_date_time: null,
  last_updated: "",
  elements: [
    {
      context_label: "ACTIVATION",
      price_components: [{ type: "FLAT", price: 0.42, vat: 19, step_size: 1 }],
    },
    {
      context_label: "ENERGY_PRICE_DAY",
      price_components: [{ type: "ENERGY", price: 0, vat: 19, step_size: 1 }],
      restrictions: { start_time: "08:00:00", end_time: "20:00:00" },
    },
    {
      context_label: "ENERGY_PRICE_NIGHT",
      price_components: [{ type: "ENERGY", price: 0, vat: 19, step_size: 1 }],
      restrictions: { start_time: "20:00:00", end_time: "08:00:00" },
    },
    {
      context_label: "BLOCKING_FEE_DAY",
      price_components: [{ type: "TIME", price: 0, vat: 19, step_size: 1 }],
      restrictions: {
        start_time: "08:00:00",
        end_time: "20:00:00",
        min_duration: 3600,
        max_duration: 28800,
      },
    },
    {
      context_label: "BLOCKING_FEE_NIGHT",
      price_components: [{ type: "TIME", price: 0, vat: 19, step_size: 1 }],
      restrictions: {
        start_time: "20:00:00",
        end_time: "08:00:00",
        min_duration: 3600,
        max_duration: 28800,
      },
    },
    {
      context_label: "BLOCKING_FEE_CAP_DAY",
      price_components: [{ type: "FLAT", price: 20.168067, vat: 19, step_size: 1 }],
      restrictions: { start_time: "08:00:00", end_time: "20:00:00", min_duration: 28800 },
    },
    {
      context_label: "BLOCKING_FEE_CAP_NIGHT",
      price_components: [{ type: "FLAT", price: 20.168067, vat: 19, step_size: 1 }],
      restrictions: { start_time: "20:00:00", end_time: "08:00:00", min_duration: 28800 },
    },
  ],
};

// ── Patch helper ───────────────────────────────────────────────────────────────

const patchElement = (element, newPrice, newMinDuration) => ({
  ...element,
  price_components: [{ ...element.price_components[0], price: newPrice }],
  ...(newMinDuration !== undefined
    ? { restrictions: { ...element.restrictions, min_duration: newMinDuration } }
    : {}),
});

// ── Apply prices to elements ───────────────────────────────────────────────────

const applyStaticPrices = (elements, prices) =>
  elements.map((el) => {
    if (el.context_label === "ENERGY_PRICE")
      return patchElement(el, toExclVat(prices.energyPrice));
    if (el.context_label === "BLOCKING_FEE")
      return patchElement(el, toExclVat(prices.blockingFeePrice), prices.blockingFeeAfter);
    return el;
  });

const applyDynamicPrices = (elements, dayPrices, nightPrices) =>
  elements.map((el) => {
    if (el.context_label === "ENERGY_PRICE_DAY")
      return patchElement(el, toExclVat(dayPrices.energyPrice));
    if (el.context_label === "ENERGY_PRICE_NIGHT")
      return patchElement(el, toExclVat(nightPrices.energyPrice));
    if (el.context_label === "BLOCKING_FEE_DAY")
      return patchElement(el, toExclVat(dayPrices.blockingFeePrice), dayPrices.blockingFeeAfter);
    if (el.context_label === "BLOCKING_FEE_NIGHT")
      return patchElement(el, toExclVat(nightPrices.blockingFeePrice), nightPrices.blockingFeeAfter);
    return el;
  });

// ── Template exports ───────────────────────────────────────────────────────────

export { STATIC_TEMPLATE, DAY_NIGHT_TEMPLATE };

// ── Main export ────────────────────────────────────────────────────────────────

/**
 * Builds a backend-ready OCPI tariff object from user-entered prices.
 *
 * @param {{ tariffType: 'static' | 'dynamic', prices: object }} params
 *   - Static prices: { energyPrice, blockingFeePrice, blockingFeeAfter }
 *     (blockingFeeAfter in seconds)
 *   - Dynamic prices: { day: { ... }, night: { ... } }
 * @returns {object} OCPI tariff ready to POST
 */
export const buildGroupTariff = ({ tariffType, prices }) => {
  if (tariffType === "dynamic") {
    return {
      ...DAY_NIGHT_TEMPLATE,
      elements: applyDynamicPrices(DAY_NIGHT_TEMPLATE.elements, prices.day, prices.night),
      last_updated: new Date().toISOString(),
    };
  }

  return {
    ...STATIC_TEMPLATE,
    elements: applyStaticPrices(STATIC_TEMPLATE.elements, prices),
    last_updated: new Date().toISOString(),
  };
};
